import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { FilterModel } from 'app/core/models/admin/filter.models';
import { Regional } from 'app/core/models/squads/territory.model';
import { DashboardStockService } from 'app/core/services/admin/dashboard-stock.service';
import { FarmsService } from 'app/core/services/admin/farms.service';
import { TerritoryService } from 'app/core/services/squads/territory.service';
import { take } from 'rxjs';
import { Subscription } from 'rxjs';
import {
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexOptions,
} from 'ng-apexcharts';
import { ScreenUtils } from 'app/shared/utils/screen-utils';

@Component({
  selector: 'app-dashboard-stock',
  templateUrl: './dashboard-stock.component.html',
  styleUrls: ['./dashboard-stock.component.scss'],
})
export class DashboardStockComponent implements OnInit, OnDestroy {
  isMobile = false;
  selectedTheme: string = 'BI';
  isLoading: boolean = false;
  host: string = '';
  farms: any[] = [];
  filters: FilterModel[] = [];
  stocksData: any;
  selectedRegional: number | null = null;
  selectedFarm: number | null = null;
  regionals: Regional[];

  overview = {
    totalRecords: 0,
    totalGlobalStock: 0,
  };
  analysisByProduct = [];
  analysisByFarm = [];
  totalBatches = 0;
  modeDistribution = { OnDemand: 0, OnFarm: 0 };
  nearingExpiryCount = 0;
  blockedCount = 0;

  loadPieChart: boolean = false;

  farmBarColors: string[] = [];

  distributionColorsPizza =
    this.selectedTheme === 'BI'
      ? ['#00720D', '#E66C37']
      : ['#31b88a', '#FF6077'];

  stockMinColorBars =
    this.selectedTheme === 'BI'
      ? ['#00720D', '#00720D']
      : ['#03B400', '#31b88a', '#26E7A6', '#FF6178', '#8B75D7'];

  stockLastMinColorBars =
    this.selectedTheme === 'BI'
      ? ['#D64550', '#D64550']
      : ['#03B400', '#31b88a', '#26E7A6', '#FF6178', '#8B75D7'];

  farmColorBars =
    this.selectedTheme === 'BI'
      ? ['#00720D']
      : ['#03B400', '#31b88a', '#26E7A6', '#FF6178', '#8B75D7'];

  distributedColors = [
    '#00720D',
    '#00720D',
    '#00720D',
    '#00720D',
    '#00720D',
    '#00720D',
    '#00720D',
    '#00720D',
    '#00720D',
    '#00720D',
  ];

  charrt: ApexOptions = {
    chart: {
      type: 'pie',
      height: 1,
    },
    series: [],
    labels: [],
    legend: { show: false },
    dataLabels: { enabled: false },
    colors: [],
  };
  barChartOptions: any = {};
  barChartFarmOptions: any = {};
  barChartBelowMinimumOptions: any = {};

  private subscription: Subscription;

  /**
   * Construtor da classe
   * @param router 
   * @param route 
   * @param _changeDetectorRef 
   * @param _farmService 
   * @param _dashboardStockService 
   * @param _territoryService 
   */
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _farmService: FarmsService,
    private _dashboardStockService: DashboardStockService,
    private _territoryService: TerritoryService
  ) {
    const currentUrl = this.router.url;

    // Redireciona se a URL for exatamente "/"
    if (currentUrl === '/') {
      this.router.navigate(['/dashboard']);
    }
  }

  /**
   * Inicialização
   */
  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    if (ScreenUtils.isTelaPequena()) {
      this.isMobile = true;
      console.log('isMobile', this.isMobile);
    }

    const largura = ScreenUtils.getLargura();
    const altura = ScreenUtils.getAltura();

    this.loadStockData();
    this.host = this._dashboardStockService.host;
    this.serviceInitialDashboard();
    this.isLoading = false;
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Controle ngOnInit
   */
  async loadStockData() {
    try {
      this.initializeFilters();
      this._changeDetectorRef.markForCheck();
    } catch (error) {
      console.error('Erro ao carregar os dados do dashboard:', error);
    }
  }

  /**
   * Carregamento Inicial do dashboard
   */
  async serviceInitialDashboard() {
    this.isLoading = true;
    try {
      this.stocksData = await this._dashboardStockService.getDashboardData();

      // Atualiza o subject se quiser reagir em outros lugares
      this._dashboardStockService.updateStockData(this.stocksData);

      this.overview = this.stocksData?.overview || {
        totalRecords: 0,
        totalGlobalStock: 0,
      };

      this.analysisByProduct = this.stocksData?.analysisByProduct || [];
      this.analysisByFarm = this.stocksData?.analysisByFarm || [];
      const batch = this.stocksData?.batchAnalysis || {};
      this.totalBatches = batch.totalBatches || 0;
      this.modeDistribution = batch.modeDistribution || {
        OnDemand: 0,
        OnFarm: 0,
      };
      this.nearingExpiryCount = batch.nearingExpiryCount || 0;
      this.blockedCount = this.stocksData?.stockEntryStatus?.blockedCount || 0;

      this.createPieChartForStockMode();
      this.createBarChartTop10Products();
      this.createBarChartTopFarms();
      this.createBarChartBelowMinimum();

      this.isLoading = false;
    } catch (error) {
      console.error('Erro ao carregar os dados do dashboard:', error);
    }
  }

  /**
   * Recarregamento apos mudanças nos filtros
   */
  async reloadDashboardWithFilters(): Promise<void> {
    this.isLoading = true;

    const filters = this.buildFilters();

    try {
      const data = await this._dashboardStockService.getDashboardData(filters);

      this.stocksData = data;
      this._dashboardStockService.updateStockData(data);

      this.overview = data?.overview || {
        totalRecords: 0,
        totalGlobalStock: 0,
      };
      this.analysisByProduct = data?.analysisByProduct || [];
      this.analysisByFarm = data?.analysisByFarm || [];
      const batch = data?.batchAnalysis || {};
      this.totalBatches = batch.totalBatches || 0;
      this.modeDistribution = batch.modeDistribution || {
        OnDemand: 0,
        OnFarm: 0,
      };
      this.nearingExpiryCount = batch.nearingExpiryCount || 0;
      this.blockedCount = data?.stockEntryStatus?.blockedCount || 0;

      this.createPieChartForStockMode();
      this.createBarChartTop10Products();
      this.createBarChartTopFarms();
      this.createBarChartBelowMinimum();
    } catch (error) {
      console.error('Erro ao recarregar o dashboard:', error);
    }

    this.isLoading = false;
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Change para Seleção de regional
   * @param regionalId
   * @param label
   */
  onChangeRegional(regionalId: number, label?: string): void {
    this.selectedRegional = regionalId;
    this.getFarms(regionalId);
    this.reloadDashboardWithFilters();
  }

  /**
   * Change para Seleção de fazendas
   * @param farmId
   * @param farmName
   */
  onChangeFarm(farmId: number, farmName: string): void {
    this.selectedFarm = farmId;
    this.reloadDashboardWithFilters();
  }

  /**
   * Seleção de fazendas pela regional selecionada
   * @param regionalId
   * @returns
   */
  async getFarms(regionalId: number): Promise<void> {
    try {
      if (!regionalId) {
        console.warn('Regional ID está vazio ou nulo.');
        this.farms = [];
        return;
      }

      const allFarms = await this._farmService.getFarms().toPromise();

      this.farms = allFarms.filter(farm => farm.territory_id === regionalId);

      if (this.farms.length === 0) {
        console.warn('Nenhuma fazenda encontrada para regional:', regionalId);
      }
    } catch (error) {
      console.error('Erro ao carregar fazendas:', error);
      this.farms = [];
    }
  }

  /**
   * Controle de inicialização para as caixas de seleção
   */
  async initializeFilters() {
    this._territoryService
      .getRegionals()
      .pipe(take(1))
      .subscribe((r: Regional[]) => {
        this.regionals = r;
      });
  }

  /**
   * Ir para a lista de estoques controlados
   */
  switchTab(): void {
    this.router.navigate(['/stock/list']);
  }

  /**
   * Grafico de pizza
   */
  createPieChartForStockMode() {
    const onDemand = this.modeDistribution.OnDemand || 0;
    const onFarm = this.modeDistribution.OnFarm || 0;

    this.charrt = {
      series: [onDemand, onFarm],
      chart: {
        type: 'pie',
        height: 400,
      },
      labels: ['OnDemand', 'OnFarm'],
      colors: this.distributionColorsPizza,
      legend: {
        show: true,
        position: 'bottom',
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number, opts: any) => {
          return `${val.toFixed(2)}%`;
        },
      },
    };

    this.loadPieChart = true;
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Top 10 Produtos perto do mínimo
   */
  createBarChartTop10Products(): void {
    const top10Products = [...this.analysisByProduct]
      .sort(
        (a, b) =>
          Math.abs(a.totalStock - a.totalMinimum) -
          Math.abs(b.totalStock - b.totalMinimum)
      )
      .slice(0, 10);

    const labels = top10Products.map(p => p.description);

    const data = top10Products.map(p => ({
      x: p.description,
      y: p.totalStock,
      min: p.totalMinimum,
    }));

    this.barChartOptions = {
      series: [
        {
          name: 'Estoque Atual',
          data: data,
        },
      ],
      chart: {
        type: 'bar',
        height: 400,
      },
      xaxis: {
        categories: labels,
        title: {
          text: 'Produtos Próximos do Mínimo',
        },
        labels: {
          style: {
            fontSize: '10px',
          },
        },
      },
      yaxis: {
        title: {
          text: 'Quantidade em Estoque',
        },
        labels: {
          style: {
            fontSize: '10px',
          },
        },
      },
      plotOptions: {
        bar: {
          distributed: true,
          columnWidth: '55%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      colors: this.stockMinColorBars,
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '10px',
          fontWeight: 'bold',
          colors: ['#FC7907'],
        },
        background: {
          enabled: true,
          color: '#FC7907',
          borderRadius: 4,
          borderWidth: 1,
          borderColor: '#FFFFFF',
          opacity: 1,
        },
        formatter: function (val, opts) {
          const point = opts?.w?.config?.series[0]?.data?.[opts.dataPointIndex];

          if (!point || point.min === undefined || point.y === undefined) {
            return `[--] ${val}`;
          }

          return `[${point.min}] ${point.y}`;
        },
        offsetY: -10,
      },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const point =
              opts?.w?.config?.series[0]?.data?.[opts.dataPointIndex];
            return `Estoque: ${point?.y} | Mínimo: ${point?.min}`;
          },
        },
      },
    };
  }

  /**
   * Top 10 - Fazendas com maiores estoques
   */
  createBarChartTopFarms(): void {
    const topFarms = [...this.analysisByFarm]
      .sort((a, b) => b.totalStock - a.totalStock)
      .slice(0, 5);

    const labels = topFarms.map((_, i) => `#${i + 1}`);
    const data = topFarms.map(f => f.totalStock);
    const total = data.reduce((acc, val) => acc + val, 0);

    this.farmBarColors = this.distributedColors.slice(0, 5);

    this.barChartFarmOptions = {
      series: [
        {
          name: 'Total em Estoque',
          data: data,
        },
      ],
      chart: {
        type: 'bar',
        height: 400,
      },
      plotOptions: {
        bar: {
          distributed: true,
          columnWidth: '55%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '10px',
          fontWeight: 'bold',
          colors: ['#FC7907'],
        },
        background: {
          enabled: true,
          color: '#FC7907',
          borderRadius: 4,
          borderWidth: 1,
          borderColor: '#FFFFFF',
          opacity: 1,
        },
        formatter: (val: number) => {
          const percentage = ((val / total) * 100).toFixed(1);
          return `[${val}] \n${percentage}%`;
        },
        offsetY: -10,
      },
      xaxis: {
        categories: labels,
        title: {
          text: 'Fazendas',
        },
        labels: {
          style: {
            fontSize: '10px',
          },
        },
      },
      yaxis: {
        title: {
          text: 'Estoque',
        },
        labels: {
          style: {
            fontSize: '10px',
          },
        },
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} unidades`,
        },
      },
      colors: this.farmColorBars,
    };
  }

  createBarChartBelowMinimum(): void {
    const belowMinimumProducts = [...this.analysisByProduct]
      .filter(p => p.belowMinimum)
      .sort((a, b) => a.totalStock - b.totalStock)
      .slice(0, 5);

    const data = belowMinimumProducts.map(p => ({
      x: p.description,
      y: p.totalStock,
      min: p.totalMinimum,
    }));

    this.barChartBelowMinimumOptions = {
      series: [
        {
          name: 'Estoque Atual',
          data,
        },
      ],
      chart: {
        type: 'bar',
        height: 400,
      },
      plotOptions: {
        bar: {
          distributed: true,
          columnWidth: '55%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      xaxis: {
        // ✅ NÃO use categories aqui
        title: { text: 'Produtos Abaixo do Mínimo' },
        labels: { style: { fontSize: '10px' } },
      },
      yaxis: {
        title: { text: 'Quantidade em Estoque' },
        labels: { style: { fontSize: '10px' } },
      },
      colors: this.stockLastMinColorBars,
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '10px',
          fontWeight: 'bold',
          colors: ['#FC7907'],
        },
        background: {
          enabled: true,
          color: '#FC7907',
          borderRadius: 4,
          borderWidth: 1,
          borderColor: '#FFFFFF',
          opacity: 1,
        },
        formatter: (val: number, opts) => {
          const point = opts?.w?.config?.series[0]?.data?.[opts.dataPointIndex];
          return point && point.min !== undefined
            ? `[${point.min}] ${point.y}`
            : `[--] ${val}`;
        },
        offsetY: -10,
      },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const point =
              opts?.w?.config?.series[0]?.data?.[opts.dataPointIndex];
            return `Estoque: ${point?.y} | Mínimo: ${point?.min}`;
          },
        },
      },
    };
  }

  /**
   * Cria filtros para reload do dashboard
   */
  buildFilters(): FilterModel[] {
    const filters: FilterModel[] = [];

    if (this.selectedRegional) {
      const selectedRegionalObj = this.regionals.find(
        r => r.id === this.selectedRegional
      );
      filters.push({
        field: 'regional_id',
        filter: this.selectedRegional.toString(),
        label: 'Regional',
        value: selectedRegionalObj?.name || this.selectedRegional.toString(),
      });
    }

    if (this.selectedFarm) {
      const selectedFarmObj = this.farms.find(f => f.id === this.selectedFarm);
      filters.push({
        field: 'farm_id',
        filter: this.selectedFarm.toString(),
        label: 'Fazenda',
        value: selectedFarmObj?.farm_name || this.selectedFarm.toString(),
      });
    }

    this.filters = filters;

    return filters;
  }

  /**
   * Remove filtros
   */
  removeFilter(filter: FilterModel): void {
    this.filters = this.filters.filter(f => f.field !== filter.field);

    if (filter.field === 'regional_id') {
      this.selectedRegional = null;
      this.selectedFarm = null;
      this.farms = [];
    }

    if (filter.field === 'farm_id') {
      this.selectedFarm = null;
    }

    this.reloadDashboardWithFilters();
  }

  /**
   * Valida se existem dados pizza
   * @returns
   */
  hasPieChartData(): boolean {
    return this.charrt?.series?.some(value => value > 0);
  }

  /**
   * Valida se tem dados top 10 produtos
   * @returns
   */
  hasBarChartop10ProductsData(): boolean {
    return (
      this.barChartOptions?.series?.[0]?.data?.length > 0 &&
      this.barChartOptions.series[0].data.some((item: any) => item.y > 0)
    );
  }

  /**
   * Valida se tem dados de fazendas
   */
  hasBarChartop10FarmsData(): boolean {
    return (
      this.barChartFarmOptions?.series?.[0]?.data?.length > 0 &&
      this.barChartFarmOptions.series[0].data.some((item: number) => item > 0)
    );
  }

  /**
   * Valida se possui dados de estoque abaixo do mínimo
   */
  hasBarChartBelowMinimumData(): boolean {
    return (
      this.barChartBelowMinimumOptions?.chart?.type &&
      this.barChartBelowMinimumOptions?.series?.[0]?.data?.length > 0
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
